import React, { useState, useCallback, useRef } from "react";
import { findUser, updateUser, updateUserCard } from "../api/userApi";
import SerialPortComponent from "../components/SerialPortComponent";
import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Search, User, CreditCard, XCircle, RefreshCw, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function ManageMember() {
    const { userData } = useAuth();
  const [user, setUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const searchInputRef = useRef(null);
  const { t } = useTranslation();

  // Search user by email, mobile, or user_name
  const searchUser = useCallback(async () => {
    if (!searchText.trim()) return;
    
    setIsSearching(true);
    try {
      const foundUser = await findUser(searchText);
      if (foundUser) {
        setUser(foundUser);
        toast.success(t('userFound'), { transition: Bounce });
      } else {
        setUser(null);
        toast.error(t('userNotFound'), { transition: Bounce });
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error(t('searchError'), { transition: Bounce });
    } finally {
      setIsSearching(false);
    }
  }, [searchText, t]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      searchUser();
    }
  };

  const handleCardData = useCallback((data) => {
    if (!data) return;
    console.log("Received card data:", data);
    
    if (data.part4 === "1") {
      setCardData(data);
      toast.info(t('cardDetected'), { transition: Bounce });
    } else if (data.part4 === "0") {
      setCardData(null);
    }
  }, [t]);

  const addCardToUser = async () => {
    if (!user || !cardData) return;
    
    setIsProcessing(true);
    try {

      const payload = {
        user_id: user.id,
        user_card: cardData.part1, // new card number
        mediator_id: userData.id,
        action: 'New',
      };
  
      const result = await updateUserCard(payload);
      console.log(result)
      setUser(result.user);
      toast.success(t('cardAddedSuccess'), { transition: Bounce });
    } catch (error) {
      console.error("Error adding card:", error);
      toast.error(t('cardAddError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const removeCard = async () => {
    if (!user || !user.user_card) return;
    
    setIsProcessing(true);
    try {
      const payload = {
        user_card: null,
      };
      const updated = await updateUser(user.id, payload);
      setUser(updated);
      toast.success(t('cardRemovedSuccess'), { transition: Bounce });
    } catch (error) {
      console.error("Error removing card:", error);
      toast.error(t('cardRemoveError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const replaceCard = async () => {
    if (!user || !cardData) return;
    
    setIsProcessing(true);
    try {
      // const payload = {
      //   user_card: cardData.part1, // New card ID
      // };
  
      // const updated = await updateUser(user.id, payload);

      const payload = {
        user_id: user.id,
        user_card: cardData.part1, // new card number
        mediator_id: userData.id,
        action: 'Replace',
      };
  
      const result = await updateUserCard(payload);
      console.log(result)
      setUser(result.user);
      toast.success(t('cardReplacedSuccess'), { transition: Bounce });
    } catch (error) {
      console.error("Error replacing card:", error);
      toast.error(t('cardReplaceError'), { transition: Bounce });
    } finally {
      setIsProcessing(false);
    }
  };

  const clearSearch = () => {
    setSearchText("");
    setUser(null);
    searchInputRef.current?.focus();
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mb-9">
    
      
      {/* Card Reader Component */}
      <div className="mb-6 flex items-center justify-between">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">
        {t('manageSubscriptions')}
      </h1>
        <SerialPortComponent onCardData={handleCardData} />
      </div>
      
      {/* Search Section */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Search size={20} className="mr-2" /> {t('findUser')}
        </h2>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              ref={searchInputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('searchPlaceholder')}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
            />
            <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
            {searchText && (
              <button 
                onClick={clearSearch}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                <XCircle size={18} />
              </button>
            )}
          </div>
          <button
            onClick={searchUser}
            disabled={!searchText.trim() || isSearching}
            className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2"
          >
            {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
            {t('search')}
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mt-2">
          {t('searchInstructions')}
        </p>
      </div>
      
      {/* User Profile Section */}
      {user && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <User size={20} className="mr-2" /> {t('userProfile')}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-3">
              <div>
                <span className="text-gray-500 text-sm">{t('fullName')}</span>
                <p className="font-medium">{user.full_name}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('username')}</span>
                <p className="font-medium">{user.user_name}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-gray-500 text-sm">{t('email')}</span>
                <p className="font-medium">{user.user_email}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('membershipCard')}</span>
                <p className="font-medium">
                  {user.user_card ? 
                    <span className="flex items-center gap-1">
                      <CreditCard size={16} className="text-green-600" />
                      {user.user_card}
                    </span> : 
                    <span className="text-gray-400">{t('noCardAssigned')}</span>
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Card Management Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">{t('cardManagement')}</h3>
            
            <div className="flex flex-wrap gap-3">
              {!user.user_card && cardData && (
                <button
                  onClick={addCardToUser}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 flex items-center gap-2"
                >
                  {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {t('addCard')}
                </button>
              )}
              
              {user.user_card && (
                <>
                  <button
                    onClick={removeCard}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300 flex items-center gap-2"
                  >
                    {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                    {t('removeCard')}
                  </button>
                  
                  {cardData && (
                    <button
                      onClick={replaceCard}
                      disabled={isProcessing}
                      className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-amber-300 flex items-center gap-2"
                    >
                      {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                      {t('replaceCard')}
                    </button>
                  )}
                </>
              )}
              
              {!cardData && (
                <p className="text-sm text-gray-500 italic flex items-center gap-1">
                  <CreditCard size={16} />
                  {t('insertCardMessage')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Card Status */}
      {cardData && (
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="flex items-start gap-3">
            <CreditCard size={20} className="text-blue-500 mt-1" />
            <div>
              <h3 className="font-medium">{t('cardDetected')}</h3>
              <p className="text-sm text-gray-600">{t('cardId')}: {cardData.part1}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}